import React from 'react';
import { css } from 'styled-components';
import { applyStyles } from 'util/helpers';
import CustomMedia from 'util/custom-media-query';
import OrderAggregatorsHeader from '../components/Header';
import FixedArea from '../components/FixedArea';
import Order from './components/Order';
import ContentHeader from './components/ContentHeader';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import moment from 'moment';
import i18n from 'i18n';
import Button from 'components/common/Button';
import SessionManager from 'util/session-manager';
import InscriptionType from 'constants/inscription-type';
import { cloneDeep } from 'lodash';
import MessageCardType from 'constants/message-card-type';
import ValidityPeriodMaxMonth from 'constants/validity-period-max-month';

class Purchase extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false
    };
  }

  async componentDidMount() {
    try {
      await this.props.fetchOrderMaster();
    } catch (error) {
      if (this.props.error) {
        const {
          showAlert,
          error: {
            data: { message }
          }
        } = this.props;
        return showAlert(message);
      }
    }
  }

  addOrder = push => {
    push('orders', this.orderTemplate);
  };

  getCost(formValue) {
    return {
      giftCard: {
        price: this.props.data.vouchers[0].price,
        count: formValue.orders.reduce(
          (result, order) => result + +order.purchaseItem.num,
          0
        )
      },
      shippingCost: {
        price: this.props.data.delivery.price,
        count: formValue.orders.filter(
          order => !order.referringOrderInfo.checked
        ).length
      }
    };
  }

  restoreData = () => {
    let savedData;
    try {
      savedData = SessionManager.load();
    } catch (error) {
      switch (error.name) {
        case 'NoSessionError':
          SessionManager.start();
          break;

        case 'SessionExpiredError':
          this.props.push('/error');
          break;

        case 'LoadDataError':
          savedData = null;
          break;

        default:
          throw error;
      }
    }

    return savedData;
  };

  async componentDidUpdate() {
    // 「ギフトを贈る」で画面遷移した場合は、データを破棄
    if (this.props.history.action === 'PUSH') {
      SessionManager.clear();
      SessionManager.start();
    }

    if (this.orderTemplate == null && this.props.data.vouchers[0] != null) {
      const { expiration, defaultDay } = this.props.data.vouchers[0];

      this.duration = moment.duration(expiration);

      this.datepickerMin = moment()
        .startOf('day')
        .add(moment.duration(defaultDay));
      this.datepickerMax = this.datepickerMin
        .clone()
        .add(ValidityPeriodMaxMonth, 'month');

      // 注文情報をセッションストレージで管理するための initial template
      this.orderTemplate = {
        purchaseItem: {
          num: 1,
          validatedOn: this.datepickerMin.format(
            i18n.t('common:dateFormat.default')
          ),
          expiredOn: this.datepickerMin
            .clone()
            .add(this.duration)
            .format(i18n.t('common:dateFormat.default'))
        },
        wrappingInfo: {
          wrappingType: i18n.t(
            'orderAggregators:common:order.wrappingType1Name'
          ),
          inscription: [],
          shipperName: [],
          messageCard: '',
          messageCardType: MessageCardType.none,
          inscriptionType: i18n.t('orderAggregators:common:order.kotobuki'),
          shipperNameType: InscriptionType.oneLine
        },
        receiver: {
          receiverName: '',
          receiverNameKana: '',
          receiverPostcode: '',
          receiverPrefecture: '',
          receiverCity: '',
          receiverBuilding: '',
          receiverCompany: '',
          receiverTelephone: ''
        },
        shipper: {
          shipperName: '',
          shipperNameKana: '',
          shipperPostcode: '',
          shipperPrefecture: '',
          shipperCity: '',
          shipperBuilding: '',
          shipperCompany: '',
          shipperTelephone: ''
        },
        referringOrderInfo: {
          checked: false,
          targetIndex: 0
        }
      };

      this.initialValues = this.restoreData() || {
        orders: [this.orderTemplate]
      };

      this.setState({
        loaded: true
      });
    }
  }

  validateOrders(orders) {
    return orders.some(
      order =>
        Object.keys(order.shipper).every(key => !order.shipper[key]) ||
        Object.keys(order.receiver).every(key => !order.receiver[key])
    );
  }

  handleClickNext = values => {
    const { data, push } = this.props;

    SessionManager.save({
      ...values,
      cost: this.getCost(values),
      masterData: data
    });
    push('/order-aggregators/payment');
  };

  createReferredOrderOptions = orders => {
    const options = [];
    orders.forEach((el, index) => {
      if (!el.referringOrderInfo.checked) {
        options.push({
          label: `${i18n.t('orderAggregators:common:order.order')}${index + 1}`,
          value: index
        });
      }
    });
    return options;
  };

  updateOrder = (orders, update, index, refIndex) => {
    const { receiver, shipper, referringOrderInfo } =
      refIndex !== null ? cloneDeep(orders[refIndex]) : this.orderTemplate;

    if (referringOrderInfo.checked) {
      update('orders', index, {
        ...orders[index],
        receiver,
        shipper,
        referringOrderInfo
      });
    } else {
      update('orders', index, {
        ...orders[index],
        receiver,
        shipper
      });
    }
  };

  updateReferringOrders = (orders, update, refIndex) => {
    orders.forEach((elm, index) => {
      if (
        elm.referringOrderInfo.checked &&
        +elm.referringOrderInfo.targetIndex === refIndex
      ) {
        this.updateOrder(orders, update, index, refIndex);
      }
    });
  };

  existsReferringOrders = (orders, index) =>
    orders.some(
      order =>
        order.referringOrderInfo.checked &&
        +order.referringOrderInfo.targetIndex === index
    );

  releaseReferringOrders = (orders, update, refIndex) => {
    orders.forEach((elm, index) => {
      if (
        elm.referringOrderInfo.checked &&
        +elm.referringOrderInfo.targetIndex === refIndex
      ) {
        const { receiver, shipper, referringOrderInfo } = this.orderTemplate;
        update('orders', index, {
          ...orders[index],
          receiver,
          shipper,
          referringOrderInfo
        });
      }
    });
  };

  copyOrder = (push, index, formValue) => {
    const targetValues = cloneDeep(formValue);

    if (!targetValues.referringOrderInfo.checked) {
      targetValues.referringOrderInfo.checked = true;
      targetValues.referringOrderInfo.targetIndex = +index;
    }

    push('orders', {
      ...this.orderTemplate,
      ...targetValues
    });
  };

  render() {
    const {
      className,
      match: { path },
      data,
      fetchAddress
    } = this.props;

    if (!this.state.loaded) {
      return null;
    }

    return (
      <div className={className}>
        <OrderAggregatorsHeader path={path} />
        <div className="order-aggregators-container">
          <Form
            onSubmit={value => this.handleClickNext(value)}
            mutators={{
              ...arrayMutators
            }}
            initialValues={this.initialValues}
            render={({ handleSubmit, submitting, invalid, form }) => (
              <form onSubmit={handleSubmit}>
                <div className="order-aggregators-content">
                  <ContentHeader price={data.vouchers[0].price} />
                  <div className="orders">
                    <FieldArray
                      name="orders"
                      validate={val => this.validateOrders(val)}
                      render={({ fields, fields: { remove } }) => (
                        <React.Fragment>
                          {fields.map((name, index) => (
                            <Order
                              key={index}
                              index={index + 1}
                              fieldName={name}
                              remove={remove}
                              datepickerMin={this.datepickerMin}
                              datepickerMax={this.datepickerMax}
                              duration={this.duration}
                              fetchAddress={fetchAddress}
                              referredOrderOptions={this.createReferredOrderOptions(
                                form.getState().values.orders
                              )}
                              existsReferringOrders={refIndex =>
                                this.existsReferringOrders(
                                  form.getState().values.orders,
                                  refIndex
                                )
                              }
                              updateOrder={refIndex =>
                                this.updateOrder(
                                  form.getState().values.orders,
                                  form.mutators.update,
                                  index,
                                  refIndex
                                )
                              }
                              updateReferringOrders={refIndex =>
                                this.updateReferringOrders(
                                  form.getState().values.orders,
                                  form.mutators.update,
                                  refIndex
                                )
                              }
                              releaseReferringOrders={refIndex =>
                                this.releaseReferringOrders(
                                  form.getState().values.orders,
                                  form.mutators.update,
                                  refIndex
                                )
                              }
                              copyable={
                                form.getState().values.orders.length < 10
                              }
                              copyOrder={(refIndex, value) =>
                                this.copyOrder(
                                  form.mutators.push,
                                  refIndex,
                                  value
                                )
                              }
                              orders={form.getState().values.orders}
                              {...form}
                            />
                          ))}
                        </React.Fragment>
                      )}
                    />
                    {form.getState().values.orders.length < 10 && (
                      <Button
                        type="button"
                        className="btn-add"
                        buttonType="E"
                        onClick={() => this.addOrder(form.mutators.push)}
                      >
                        {i18n.t('orderAggregators:purchase:addOrder')}
                      </Button>
                    )}
                  </div>
                </div>

                <FixedArea
                  buttonText={i18n.t('orderAggregators:purchase:submit')}
                  displayQuotation={true}
                  history={this.props.history}
                  cost={this.getCost(form.getState().values)}
                  disabled={invalid || submitting}
                />
              </form>
            )}
          />
        </div>
      </div>
    );
  }
}

const commonStyle = css`
  .btn-add {
    display: block;
    margin: 0 auto;
  }
  @media all and (-ms-high-contrast: none) and (min-width:1025px) {
    .order-aggregators-content {
      max-width: 60% !important;
    }
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  .order-aggregators-container {
    max-width: 1366px;
    padding: 72px 80px 160px;
    margin: 0 auto;
  }

  form {
    display: flex;
  }

  .order-aggregators-content {
    max-width: 762px;
    margin-right: 136px;
  }

  .orders {
    max-width: 762px;
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  .order-aggregators-container {
    margin: 0 16px;
  }

  .order-aggregators-content {
    margin: 16px 0 32px;
  }

  ${FixedArea} {
    margin: 0 -16px;
  }
`;

export default applyStyles(Purchase)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/Purchase/index.js